import { MILLISECONDS_IN_MINUTE } from '@stdlib/constants-time';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useMemo } from 'react';

import type {
  FormInventorySaved,
  TimePeriod,
} from 'app/components/pages/Extranet/ExtranetInventory/Legacy/type';
import type { HotelInventory } from 'app/typings/Extranet/inventory';
import type { Hotel } from 'app/typings/hotels/Hotel/Hotel';
import type { ExtranetExperience, ExtranetPackage } from 'app/typings/packages';
import { apiMutation, apiRequest } from 'app/utils/request/api';

import { formatInventory } from './format/inventory';

export const getInventoryPath = '/extranet/:hotelId/inventory' as const;

const validateInventoryPath = '/extranet/:hotelId/inventory/validate' as const;
const saveHotelInventoryPath = '/extranet/:hotelId/inventory' as const;

export function useGetHotelInventory(
  startDate: string,
  timePeriod: TimePeriod,
  getExperiencesForPackage: (packageId: number) => ExtranetExperience[],
  packages: ExtranetPackage[],
  hasAtLeastOneDayPackage: boolean,
  hotelInfo?: Hotel
) {
  const queryKey = [getInventoryPath, { hotelId: hotelInfo?.id, startDate }];

  const query = useQuery<HotelInventory>({
    queryKey,
    queryFn: !!hotelInfo?.id
      ? () =>
          apiRequest({
            path: getInventoryPath,
            params: { hotelId: hotelInfo?.id?.toString() },
            queryParams: { startDate },
          }).catch((err) =>
            message.error('Error fetching hotel inventory', err)
          )
      : undefined,
    enabled: !!hotelInfo?.id,
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
    keepPreviousData: false,
  });

  const formattedInventory = useMemo(() => {
    if (query.data) {
      return formatInventory(
        query.data,
        timePeriod,
        getExperiencesForPackage,
        packages,
        hasAtLeastOneDayPackage,
        hotelInfo
      );
    }

    return undefined;
  }, [
    query.data,
    timePeriod,
    getExperiencesForPackage,
    packages,
    hotelInfo,
    hasAtLeastOneDayPackage,
  ]);

  return {
    ...query,
    data: formattedInventory,
  };
}

export function useValidateHotelInventory(hotelId: number, startDate: string) {
  return useMutation({
    mutationFn: async (payload: FormInventorySaved) =>
      apiMutation(
        'POST',
        {
          path: validateInventoryPath,
          params: { hotelId: hotelId.toString() },
        },
        payload
      ),
    onError: () => {
      message.error('Error saving inventory');
    },
  });
}

export function useSaveHotelInventory(hotelId: number, startDate: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: FormInventorySaved) =>
      apiMutation(
        'POST',
        {
          path: saveHotelInventoryPath,
          params: { hotelId: hotelId.toString() },
        },
        payload
      ),
    onSuccess: () => {
      queryClient.invalidateQueries([getInventoryPath, { hotelId, startDate }]);
      message.success('Inventory successfully saved');
    },
    onError: () => {
      message.error('Error saving inventory');
    },
  });
}
